import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Link2, ExternalLink, ArrowLeft } from "lucide-react";
import api from "../api/client";
import { useAuth } from "../contexts/AuthContext";

const EVENT_COLOR = {
    ORDER_PLACED: "text-cyan-400 border-cyan-500/30 bg-cyan-500/10",
    ORDER_MATCHED: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
    MARGIN_ADDED: "text-amber-400 border-amber-500/30 bg-amber-500/10",
    EARLY_EXIT: "text-orange-400 border-orange-500/30 bg-orange-500/10",
    LIQUIDATION: "text-rose-400 border-rose-500/30 bg-rose-500/10",
    SETTLEMENT: "text-violet-400 border-violet-500/30 bg-violet-500/10",
};

export default function MyOnChain() {
    const { user } = useAuth();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        const load = () => api.get("/me/attestations?limit=200")
            .then((r) => { if (mounted) { setEvents(r.data || []); setLoading(false); } })
            .catch(() => { if (mounted) setLoading(false); });
        load();
        const id = setInterval(load, 10000);
        return () => { mounted = false; clearInterval(id); };
    }, []);

    const counts = events.reduce((acc, ev) => {
        acc[ev.event_type] = (acc[ev.event_type] || 0) + 1;
        return acc;
    }, {});

    return (
        <div className="bg-[#050505] min-h-[calc(100vh-3.5rem)]">
            <div className="max-w-[1100px] mx-auto px-5 sm:px-6 lg:px-8 py-8 sm:py-10">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">
                            My Hyperliquid testnet footprint
                        </div>
                        <h1 className="mt-2 text-2xl sm:text-3xl font-medium tracking-tighter">
                            Your on-chain activity
                        </h1>
                        <div className="mt-1.5 text-[11px] font-mono text-zinc-500 break-all">
                            Destination wallet · <span className="text-zinc-300">{user?.wallet_address}</span>
                        </div>
                    </div>
                    <Link
                        to="/positions"
                        data-testid="myonchain-back"
                        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-zinc-400 hover:text-cyan-400 border border-[#1F1F24] hover:border-cyan-500/40 px-3 py-2 rounded-sm"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" /> Positions
                    </Link>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-6">
                    {[
                        ["ORDER_PLACED", "Placed"],
                        ["ORDER_MATCHED", "Matched"],
                        ["MARGIN_ADDED", "Margin"],
                        ["EARLY_EXIT", "Exits"],
                        ["LIQUIDATION", "Liquid."],
                        ["SETTLEMENT", "Settled"],
                    ].map(([key, label]) => (
                        <div
                            key={key}
                            data-testid={`stat-${key.toLowerCase()}`}
                            className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm p-3"
                        >
                            <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-zinc-500">{label}</div>
                            <div className="mt-1 text-xl font-mono text-zinc-100">{counts[key] || 0}</div>
                        </div>
                    ))}
                </div>

                <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm overflow-hidden">
                    <div className="px-4 py-3 border-b border-[#1F1F24] flex items-center justify-between">
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                            All your on-chain attestations ({events.length})
                        </div>
                        <Link to="/onchain" className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 hover:text-cyan-300">
                            Public tape →
                        </Link>
                    </div>
                    {loading && (
                        <div className="px-5 py-10 text-center text-[11px] font-mono text-zinc-600">loading…</div>
                    )}
                    {!loading && events.length === 0 && (
                        <div className="px-5 py-10 text-center text-[11px] font-mono text-zinc-600">
                            No on-chain events yet — place a swap and it'll show up here.
                        </div>
                    )}
                    <div className="divide-y divide-[#1F1F24]">
                        {events.map((ev) => {
                            const color = EVENT_COLOR[ev.event_type] || "text-zinc-400 border-zinc-700 bg-zinc-700/10";
                            return (
                                <a
                                    key={ev.id}
                                    href={ev.explorer_url || "#"}
                                    target={ev.explorer_url ? "_blank" : "_self"}
                                    rel="noopener noreferrer"
                                    data-testid={`myonchain-row-${ev.id}`}
                                    className="block px-4 sm:px-5 py-3 hover:bg-[#0F0F12]/60 transition-colors"
                                >
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span className={`shrink-0 text-[9.5px] font-mono uppercase tracking-[0.2em] px-2 py-1 rounded-sm border ${color}`}>
                                            {ev.event_type.replace("_", " ")}
                                        </span>
                                        <span className="font-mono text-[11px] text-zinc-500 shrink-0">
                                            {new Date(ev.attempted_at).toLocaleString()}
                                        </span>
                                        {ev.metadata?.coin && (
                                            <span className="font-mono text-[11px] text-zinc-300 shrink-0">{ev.metadata.coin}</span>
                                        )}
                                        <div className="flex-1" />
                                        {ev.tx_hash ? (
                                            <span className="inline-flex items-center gap-1 font-mono text-[10px] text-cyan-400/80 shrink-0">
                                                <Link2 className="w-3 h-3" />
                                                <span className="truncate max-w-[140px] sm:max-w-[220px]">{ev.tx_hash}</span>
                                                <ExternalLink className="w-3 h-3" />
                                            </span>
                                        ) : (
                                            <span className="font-mono text-[10px] text-amber-400/80 shrink-0 truncate max-w-[200px]">
                                                pending · {ev.error || ""}
                                            </span>
                                        )}
                                    </div>
                                </a>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
